import Service, { service } from '@ember/service';
import { addListener, removeListener } from '@ember/object/events';
import { queryManager } from 'ember-apollo-client';
import { variation } from 'ember-launch-darkly';
import { capitalize } from '@ember/string';
import * as Sentry from '@sentry/browser';
import * as calendarCache from 'my-phorest/utils/calendar-cache';

import basketCheckoutChange from 'my-phorest/gql/subscriptions/basket-checkout-change.graphql';
import basketChange from 'my-phorest/gql/subscriptions/basket-change.graphql';
import calendarChange from 'my-phorest/gql/subscriptions/calendar-change.graphql';
import payrollRunDone from 'my-phorest/gql/subscriptions/payroll-run-done.graphql';
import printRequest from 'my-phorest/gql/subscriptions/print-request.graphql';
import rostersChange from 'my-phorest/gql/subscriptions/rosters-change.graphql';
import chatChange from 'my-phorest/gql/subscriptions/chat-change.graphql';
import notificationCenterChange from 'my-phorest/gql/subscriptions/notification-center-change.graphql';
import backgroundTaskChange from 'my-phorest/gql/subscriptions/background-task-change.graphql';
import appointmentsQuery from 'my-phorest/gql/queries/appointments.graphql';
import basketQuery from 'my-phorest/gql/queries/basket.graphql';
import breaksQuery from 'my-phorest/gql/queries/breaks.graphql';

export default class ApolloSubscriptions extends Service {
  @queryManager apollo;
  @service columnsPrinter;
  @service electronApp;
  @service electronAppPrinter;
  @service eventPropagator;
  @service purchaseReceipt;
  @service selectedNotePrinter;
  @service versionTracker;
  @service voucherReceipt;

  calendarChangeSubscription = null;
  basketCheckoutChangeSubscription = null;
  basketChangeSubscription = null;
  payrollRunDoneSubscription = null;
  printRequestSubscription = null;
  rostersChangeSubscription = null;
  chatChangeSubscription = null;
  notificationCenterChangeSubscription = null;
  backgroundTaskChangeSubscriptions = {};

  constructor() {
    super(...arguments);

    this._handleBasketChangeNotification =
      this.handleBasketChangeNotification.bind(this);

    this._handleBasketCheckoutNotification =
      this.handleBasketCheckoutNotification.bind(this);

    this._handleCalendarNotification =
      this.handleCalendarNotification.bind(this);

    this._handlePayrollRunDoneNotification =
      this.handlePayrollRunDoneNotification.bind(this);

    this._handlePrintRequestNotification =
      this.handlePrintRequestNotification.bind(this);

    this._handleRostersNotification = this.handleRostersNotification.bind(this);

    this._handleChatChange = this.handleChatChange.bind(this);

    this._handleNotificationCenterChange =
      this.handleNotificationCenterChange.bind(this);

    this._handleBackgroundTaskChangeNotification =
      this.handleBackgroundTaskChangeNotification.bind(this);
  }

  async handleBackgroundTaskChangeNotification(event) {
    let { resource } = event;
    this.eventPropagator.broadcast('backgroundTaskChange', resource);
  }

  async handleBasketChangeNotification(event) {
    if (this.#shouldIgnoreNotification(event.sourceApplication)) return;
    this._fetchBasket(event.resource.id);
  }

  async handleBasketCheckoutNotification(event) {
    const { action, resource, error } = event;
    this.eventPropagator.broadcast('basketCheckoutChange', {
      action,
      resourceType: resource.type,
      error,
    });
  }

  async handleChatChange(event) {
    const { action, resource, error } = event;
    this.eventPropagator.broadcast('clientChatChange', {
      action,
      resource,
      error,
    });
  }

  async handleNotificationCenterChange(event) {
    const { action, resource, error } = event;
    if (resource?.notificationType === 'NEW_LEAD') {
      this.eventPropagator.broadcast('leadNotificationChange', {
        action,
        resource,
        error,
      });
    }
  }

  async handleCalendarNotification(event) {
    if (this.#shouldIgnoreNotification(event.sourceApplication)) return;

    const { action, resource } = event;
    let data;

    Sentry.addBreadcrumb({
      category: 'WebSockets',
      message: `WS notification: ${resource.type} (${resource.id}) ${action}`,
      level: 'info',
    });

    if (action === 'CREATED' || action === 'UPDATED') {
      if (resource.type === 'APPOINTMENT') {
        data = await this._fetchAppointment(resource.id);
      }
      if (resource.type === 'BREAK') {
        data = await this._fetchBreak(resource.id);
      }
    } else if (action === 'DELETED' || action === 'CANCELLED') {
      let __typename;
      if (resource.type === 'APPOINTMENT') {
        __typename = 'Appointment';
      } else if (resource.type === 'BREAK') {
        __typename = 'Break';
      } else {
        return;
      }
      data = [{ __typename, id: resource.id }];
    }

    const { cache } = this.apollo.apolloClient;
    this.updateCalendarCache(cache, data, action, resource.type);
    this.eventPropagator.broadcast('calendarChange', {
      data,
      cache,
      action,
      resourceType: resource.type,
    });
  }

  async handlePayrollRunDoneNotification(event) {
    this.eventPropagator.broadcast('payrollRunDone', event);
  }

  async handlePrintRequestNotification(event) {
    const printType = event.resource.type?.toLowerCase();
    if (printType === 'appointment_note') {
      return this.selectedNotePrinter.print(
        {
          clientId: event.resource.clientId,
          date: event.resource.date,
          branchId: event.resource.branchId,
        },
        { forceLocalPrinting: true }
      );
    } else if (printType === 'appointment_reminder') {
      return this.electronAppPrinter.printAppointmentReminderTask.perform(
        event.resource.appointmentId,
        { branchId: event.resource.branchId, forceLocalPrinting: true }
      );
    } else if (printType === 'client_travel_card') {
      return this.electronAppPrinter.printClientTravelCardTask.perform(
        event.resource.appointmentId,
        { branchId: event.resource.branchId, forceLocalPrinting: true }
      );
    } else if (printType === 'client_quote') {
      return this.electronAppPrinter.printClientQuoteTask.perform(
        event.resource.clientId,
        event.resource.date,
        { branchId: event.resource.branchId, forceLocalPrinting: true }
      );
    } else if (printType === 'open_cash_drawer') {
      return this.electronApp.openCashDrawer({ forceLocalOpening: true });
    } else if (printType === 'purchase') {
      const promises = [
        this.purchaseReceipt.printReceiptInElectron(event.resource.purchaseId, {
          branchId: event.resource.branchId,
          forceLocalPrinting: true,
        }),
      ];
      if (event.resource.openCashDrawer) {
        promises.push(
          this.electronApp.openCashDrawer({ forceLocalOpening: true })
        );
      }
      return Promise.all(promises);
    } else if (printType === 'staff_members_column') {
      return this.columnsPrinter.print(
        {
          date: event.resource.date,
          staffMemberIds: event.resource.staffMemberIds,
          withNotes: event.resource.withNotes,
        },
        { branchId: event.resource.branchId, forceLocalPrinting: true }
      );
    } else if (printType === 'vouchers') {
      return this.voucherReceipt.printInElectronTask.perform(
        event.resource.voucherIds,
        { branchId: event.resource.branchId, forceLocalPrinting: true }
      );
    }
  }

  async handleRostersNotification(event) {
    if (this.#shouldIgnoreNotification(event.sourceApplication)) return;

    const { action, resource } = event;

    this.eventPropagator.broadcast('rostersChange', {
      action,
      resourceType: resource.type,
    });
  }

  updateCalendarCache(cache, data, action, resourceType) {
    if (action === 'CREATED') {
      data.forEach((event) => {
        const eventType = capitalize(resourceType.toLowerCase()),
          eventId = event.id,
          date = event.date;

        if (event.staffMemberId) {
          calendarCache.addEvent(cache, {
            date,
            eventId,
            eventType,
            resourceId: event.staffMemberId,
          });
        }
        if (event.machine?.id) {
          calendarCache.addEvent(cache, {
            date,
            eventId,
            eventType,
            resourceId: event.machine.id,
            resourceType: 'Machine',
          });
        }
        if (event.room?.id) {
          calendarCache.addEvent(cache, {
            date,
            eventId,
            eventType,
            resourceId: event.room.id,
            resourceType: 'Room',
          });
        }
      });
    }

    if (action === 'UPDATED') {
      calendarCache.potentiallyMoveEventsWithUnknownOrigin(cache, data);
    }

    if (action === 'DELETED' || action === 'CANCELLED') {
      data.forEach(({ id, __typename }) => {
        const normalizedId = cache.identify({ id, __typename });
        if (normalizedId) {
          cache.evict({ id: normalizedId });
        }
      });
    }

    cache.evict({ id: 'ROOT_QUERY', fieldName: 'appointmentSearch' });
    cache.gc();
  }

  async subscribe(subscriptionName, variables = {}) {
    if (subscriptionName === 'backgroundTaskChange') {
      if (!variables?.taskId) {
        console.warn(
          'ApolloSubscriptions @ subscribe: needs variables.taskId to subscribe "backgroundTaskChange"'
        );
        return;
      }

      if (this.backgroundTaskChangeSubscriptions[variables.taskId]) {
        console.warn(
          `ApolloSubscriptions @ subscribe: already subscribed to backgroundTaskChange with taskId "${variables.taskId}"`
        );
        return;
      }

      let subscription = await this.apollo.subscribe(
        { query: backgroundTaskChange, variables },
        'backgroundTaskChange'
      );

      addListener(
        subscription,
        'event',
        this._handleBackgroundTaskChangeNotification
      );
      this.backgroundTaskChangeSubscriptions[variables.taskId] = subscription;
    }

    if (subscriptionName === 'basketChange') {
      this.basketChangeSubscription = await this.apollo.subscribe(
        { query: basketChange, variables },
        'basketChange'
      );

      addListener(
        this.basketChangeSubscription,
        'event',
        this._handleBasketChangeNotification
      );
    }

    if (subscriptionName === 'basketCheckoutChange') {
      this.basketCheckoutChangeSubscription = await this.apollo.subscribe(
        { query: basketCheckoutChange, variables },
        'basketCheckoutChange'
      );

      addListener(
        this.basketCheckoutChangeSubscription,
        'event',
        this._handleBasketCheckoutNotification
      );
    }

    if (subscriptionName === 'calendarChange') {
      this.calendarChangeSubscription = await this.apollo.subscribe(
        { query: calendarChange, variables },
        'calendarChange'
      );

      addListener(
        this.calendarChangeSubscription,
        'event',
        this._handleCalendarNotification
      );
    }

    if (subscriptionName === 'payrollRunDone') {
      this.payrollRunDoneSubscription = await this.apollo.subscribe(
        { query: payrollRunDone, variables },
        'payrollRunDone'
      );

      addListener(
        this.payrollRunDoneSubscription,
        'event',
        this._handlePayrollRunDoneNotification
      );
    }

    if (subscriptionName === 'printRequest') {
      if (!variation('release-electron-remote-printing')) return;

      this.printRequestSubscription = await this.apollo.subscribe(
        { query: printRequest, variables },
        'printRequest'
      );

      addListener(
        this.printRequestSubscription,
        'event',
        this._handlePrintRequestNotification
      );
    }

    if (subscriptionName === 'rostersChange') {
      this.rostersChangeSubscription = await this.apollo.subscribe(
        { query: rostersChange, variables },
        'rostersChange'
      );

      addListener(
        this.rostersChangeSubscription,
        'event',
        this._handleRostersNotification
      );
    }

    if (subscriptionName === 'chatChange') {
      this.chatChangeSubscription = await this.apollo.subscribe(
        { query: chatChange, variables },
        'chatChange'
      );

      addListener(this.chatChangeSubscription, 'event', this._handleChatChange);
    }

    if (subscriptionName === 'leadNotificationChange') {
      this.notificationCenterChangeSubscription = await this.apollo.subscribe(
        { query: notificationCenterChange, variables },
        'notificationCenterChange'
      );

      addListener(
        this.notificationCenterChangeSubscription,
        'event',
        this._handleNotificationCenterChange
      );
    }
  }

  unsubscribe(subscriptionName, variables) {
    if (
      subscriptionName === 'basketCheckoutChange' &&
      this.basketCheckoutChangeSubscription
    ) {
      this.basketCheckoutChangeSubscription.apolloUnsubscribe();
      removeListener(
        this.basketCheckoutChangeSubscription,
        'event',
        this._handleBasketCheckoutNotification
      );
      this.basketCheckoutChangeSubscription = null;
    }

    if (
      subscriptionName === 'calendarChange' &&
      this.calendarChangeSubscription
    ) {
      this.calendarChangeSubscription.apolloUnsubscribe();
      removeListener(
        this.calendarChangeSubscription,
        'event',
        this._handleCalendarNotification
      );
      this.calendarChangeSubscription = null;
    }

    if (subscriptionName === 'basketChange' && this.basketChangeSubscription) {
      this.basketChangeSubscription.apolloUnsubscribe();
      removeListener(
        this.basketChangeSubscription,
        'event',
        this._handleBasketChangeNotification
      );
      this.basketChangeSubscription = null;
    }

    if (subscriptionName === 'chatChange' && this.chatChangeSubscription) {
      this.chatChangeSubscription.apolloUnsubscribe();
      removeListener(
        this.chatChangeSubscription,
        'event',
        this._handleChatChange
      );
      this.chatChangeSubscription = null;
    }

    if (
      subscriptionName === 'leadNotificationChange' &&
      this.notificationCenterChangeSubscription
    ) {
      this.notificationCenterChangeSubscription.apolloUnsubscribe();
      removeListener(
        this.notificationCenterChangeSubscription,
        'event',
        this._handleNotificationCenterChange
      );
      this.notificationCenterChangeSubscription = null;
    }

    if (
      subscriptionName === 'payrollRunDone' &&
      this.payrollRunDoneSubscription
    ) {
      this.payrollRunDoneSubscription.apolloUnsubscribe();
      removeListener(
        this.payrollRunDoneSubscription,
        'event',
        this._handlePayrollRunDoneNotification
      );
      this.payrollRunDoneSubscription = null;
    }

    if (subscriptionName === 'printRequest' && this.printRequestSubscription) {
      this.printRequestSubscription.apolloUnsubscribe();
      removeListener(
        this.printRequestSubscription,
        'event',
        this._handlePrintRequestNotification
      );
      this.printRequestSubscription = null;
    }

    if (
      subscriptionName === 'rostersChange' &&
      this.rostersChangeSubscription
    ) {
      this.rostersChangeSubscription.apolloUnsubscribe();
      removeListener(
        this.rostersChangeSubscription,
        'event',
        this._handleRostersNotification
      );
      this.rostersChangeSubscription = null;
    }

    if (subscriptionName === 'backgroundTaskChange') {
      if (!variables?.taskId) {
        console.warn(
          'ApolloSubscriptions @ unsubscribe: needs variables to unsubscribe "backgroundTaskChange" subscription'
        );
        return;
      }

      let subscription =
        this.backgroundTaskChangeSubscriptions[variables.taskId];
      if (!subscription) {
        return;
      }

      subscription.apolloUnsubscribe();
      removeListener(
        subscription,
        'event',
        this._handleBackgroundTaskChangeNotification
      );
      delete this.backgroundTaskChangeSubscriptions[variables.taskId];
    }
  }

  unsubscribeAll() {
    this.unsubscribe('basketCheckoutChange');
    this.unsubscribe('calendarChange');
    this.unsubscribe('chatChange');
    this.unsubscribe('notificationCenterChange');
    this.unsubscribe('basketChange');
    this.unsubscribe('payrollRunDone');
    this.unsubscribe('printRequest');
    this.unsubscribe('rostersChange');
    this.unsubscribeAllBackgroundTaskChange();
  }

  unsubscribeAllBackgroundTaskChange() {
    Object.keys(this.backgroundTaskChangeSubscriptions).forEach((taskId) => {
      this.unsubscribe('backgroundTaskChange', { taskId });
    });
  }

  async _fetchAppointment(id) {
    const { appointments } = await this.apollo.query({
      query: appointmentsQuery,
      variables: {
        filterBy: {
          ids: [id],
        },
      },
      fetchPolicy: 'network-only',
    });

    if (appointments) {
      return appointments.edges.map((edge) => edge.node);
    }
    return null;
  }

  async _fetchBreak(id) {
    const { breaks } = await this.apollo.query({
      query: breaksQuery,
      variables: {
        filterBy: {
          ids: [id],
        },
      },
      fetchPolicy: 'network-only',
    });

    if (breaks) {
      return breaks.edges.map((edge) => edge.node);
    }
    return null;
  }

  /**
   * Ignores notifications that were caused by this application instance.
   *
   * @param sourceApplication
   * @param {string} sourceApplication.id - application id (sent to the server with every request via 'x-phorest-application-id' header)
   * @param {string} sourceApplication.instanceId - application instance id (sent to the server with every request via 'x-phorest-application-instance-id' header)
   * @returns {boolean}
   */
  #shouldIgnoreNotification(sourceApplication) {
    if (!sourceApplication) return false;

    return (
      sourceApplication.id === 'my-phorest' &&
      sourceApplication.instanceId === this.versionTracker.applicationInstanceId
    );
  }

  async _fetchBasket(id) {
    await this.apollo.query(
      {
        query: basketQuery,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      },
      'basket'
    );
  }
}
